import { connect } from "react-redux";
import { RootState } from "../../../state/configureStore";
import { useEffect, useRef, useState } from "react";
import AnalyticsService from "../../../services/analytics/AnalyticsService";
import Loading from "../../Loading/Loading";
import { Redirect, Route } from "react-router-dom";
import { showErrorModal } from "../../Modals/InformationModalReducer";
import Assessment from "../../../interfaces/Assessment";
import FirebaseService from "../../../services/firebase/FirebaseService";
import ReaderView from "../BookScreen/ReaderPage";
import Book from "../../../interfaces/Book";
import Page from "../../../interfaces/Page";
import AssessmentGradeLevel from "../../../interfaces/AssessmentGradeLevel";
import { Paper, Typography } from "@material-ui/core";
import RecorderService from "../../../services/recorder/RecorderService";
import UploadService from "../../../services/upload/UploadService";
import LogService from "../../../services/LogService";
import { ProviderContext } from "notistack";
import RecordedPage from "../../../interfaces/RecordedPage";
import StudentHeader from "../../StudentHeader/StudentHeader";
import BookScreenButton from "../BookScreen/BookScreenButton";

interface ScreenState {
  books: Map<string, Book>;
  recorded_pages: Map<string, RecordedPage[]>;
  pages: Map<string, Page[]>;
  currentScreen: number;
  currentBook?: Book;
  currentPage?: Page;
  currentGradeLevel?: AssessmentGradeLevel;
  isRecording: boolean;
  recordingSectionNumber: number;
  isOnWaitEntry: boolean;
  currentAssessmentLevel: number;
  currentReadingScreen: number;
  assessmentCompleted: boolean;
}

type AssessmentScreenProps = ReturnType<typeof mapStateToProps> & ProviderContext;
function AssessmentScreen(props: AssessmentScreenProps) {
  const [assessment, setAssessment] = useState<Assessment | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const recorder = useRef(new RecorderService());

  const [state, setState] = useState<ScreenState>({
    books: new Map<string, Book>(),
    pages: new Map<string, Page[]>(),
    recorded_pages: new Map<string, RecordedPage[]>(),
    currentScreen: 1,
    currentBook: undefined,
    currentPage: undefined,
    currentGradeLevel: undefined,
    isRecording: false,
    recordingSectionNumber: 0,
    isOnWaitEntry: false,
    currentAssessmentLevel: 0,
    currentReadingScreen: 0,
    assessmentCompleted: false,
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // Log the screen view
  useEffect(() => {
    AnalyticsService.logScreenView();
    if (!props.isConnectingUser && props.profile && props.userSettings && !recorder.current.initialized) {
      recorder.current.initialize(
        45 * 1000,
        props.userSettings.audioDevice,
        async (data, duration) => {
          // Move it to its own thread so that it doesn't hold up the student
          const currentBook = stateRef.current.currentBook;
          const currentPage = stateRef.current.currentPage;

          const promise = new Promise<void>(async (resolve, reject) => {
            try {
              const uploadPath = await FirebaseService.initializeSubmissionPage(
                props.userId,
                currentBook?.id || "",
                currentPage?.navigation?.id || "",
                "Assessment",
                currentPage?.content || "",
              );

              await FirebaseService.initializeRecordingPage(
                props.userId,
                currentBook?.id || "",
                currentPage?.navigation?.id || "",
                stateRef.current.recordingSectionNumber,
              );

              await UploadService.uploadAudioData(
                data,
                uploadPath + `/s${stateRef.current.recordingSectionNumber}`,
                Math.floor(duration / 100) / 10,
              );

              await FirebaseService.submitRecordingPage(
                props.userId,
                currentBook?.id || "",
                currentPage?.navigation?.id || "",
              );

              resolve();
            } catch (e) {
              reject(e);
            }
          });

          promise.catch(e => {
            LogService.exception(e, `AssessmentScreen.startRecording`, "Failed to initialize recording page");
            showErrorModal("Recording Failed", "Could not upload your recording");
          });
        },
        () => {
          stopRecording(true);
        },
      );

      FirebaseService.getAssessment("H5ZPiI8clyFsNVKahQMk").then(async assessment => {
        setAssessment(assessment);

        // Load all of the books and pages
        await Promise.all(
          assessment.grades.map(async grade => {
            const book = await FirebaseService.getBook(grade.bookId);
            const pages = await FirebaseService.getPages(grade.bookId);

            await Promise.all(
              pages.map(
                async page => await FirebaseService.deleteRecordingPage(props.userId, grade.bookId, page.navigation.id),
              ),
            );

            // Start watches on the submissions for this book
            FirebaseService.watchStudentRecordedPages(props.userId, book.id, async recordedPages => {
              // console.log('Pages received');
              // console.log(recordedPages);
              setState(oldState => {
                const newState = {
                  ...oldState,
                };
                newState.recorded_pages.set(grade.bookId || "", recordedPages);
                return newState;
              });
            });

            setState(oldState => {
              const newState = {
                ...oldState,
              };

              newState.books.set(grade.bookId, book);
              newState.pages.set(grade.bookId, pages);

              return newState;
            });
          }),
        );

        setState(oldState => {
          return {
            ...oldState,
            currentGradeLevel: assessment.grades[0],
            currentBook: oldState.books.get(assessment?.grades[0]?.bookId || ""),
            currentPage: oldState.pages
              .get(assessment?.grades[0]?.bookId || "")
              ?.find(page => page.navigation.pageNum === 1),
          };
        });
        setIsLoading(false);
      });
    }
  }, [props.userId, props.isConnectingUser, props.profile, props.userSettings]);

  // If we're loading the current user, just return a loading spinner
  if (props.isConnectingUser) {
    return <Loading />;
  }

  // Ensure the user is logged in
  if (!props.profile) {
    return <Route component={() => <Redirect to={{ pathname: "/Login" }} />} render={undefined} />;
  }

  // Ensure the user has access
  if (!props.profile.canTakeAssessment) {
    return <Route component={() => <Redirect to={{ pathname: "/Home" }} />} render={undefined} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  if (!assessment || !state.currentPage) {
    return <div>Egad, assessment not found!</div>;
  }

  const startRecording = () => {
    setState(state => {
      return {
        ...state,
        isRecording: true,
      };
    });

    recorder.current.startRecording();
  };

  const stopRecording = async (showMessage: boolean) => {
    setState(state => {
      return {
        ...state,
        isRecording: false,
      };
    });

    await recorder.current.stopRecording();

    if (showMessage) {
      // props.enqueueSnackbar('Recording Stopped', {variant: 'success', autoHideDuration: 5000});
    }
  };

  interface ReadingResults {
    accuracy: number;
    wordsPerMinute: number;
    completionPercentage: number;
  }

  const getAccuracy = (): ReadingResults | undefined => {
    console.log(`Accuracy Screen ${state.currentScreen}`);
    const bookId = state.currentGradeLevel?.bookId || "";
    const pages = state.pages.get(bookId);
    const page = pages?.find(page => page.navigation.pageNum === state.currentScreen - 1);
    if (page?.navigation?.id) {
      const recorded_pages = state.recorded_pages.get(bookId);
      if (recorded_pages) {
        const recorded_page = recorded_pages.find(obj => obj.pageId === page?.navigation.id);

        if (recorded_page) {
          console.log("Recorded Page");
          console.log(recorded_page);
          return {
            accuracy: recorded_page.accuracy,
            wordsPerMinute: recorded_page.wordsPerMin,
            completionPercentage: recorded_page.completionPercentage,
          };
        }
      }
    }
    return;
  };

  const hasPreviousResults = () => {
    const bookId = state.currentGradeLevel?.bookId || "";
    const pages = state.pages.get(bookId);
    const page = pages?.find(page => page.navigation.pageNum === state.currentScreen - 1);
    if (page?.navigation?.id) {
      const recorded_pages = state.recorded_pages.get(bookId);
      if (recorded_pages) {
        const recorded_page = recorded_pages.find(obj => obj.pageId === page?.navigation.id);

        if (recorded_page) {
          return true;
        }
      }
    }

    return false;
  };

  const nextRecording = async () => {
    await stopRecording(true);
    startRecording();

    let nextLevel = state.currentAssessmentLevel;

    if (state.isOnWaitEntry) {
      // If we're on a wait entry, we need to calculate where we go next.
      const results = getAccuracy();

      if (results) {
        if (results.completionPercentage > 0.9 && results.accuracy > 0.9 && results.wordsPerMinute > 75) {
          if (nextLevel < (assessment?.grades?.length || 0) - 1) {
            nextLevel += 1;
          }
        } else {
          if (nextLevel > 0) {
            nextLevel -= 1;
          }
        }
      }
    } else {
      // We're not on a wait entry, move horizontally
    }

    setState(oldState => {
      return {
        ...oldState,
        currentScreen: oldState.currentScreen + 1,
        currentAssessmentLevel: nextLevel,
        currentGradeLevel: assessment?.grades[nextLevel],
        isOnWaitEntry: !oldState.isOnWaitEntry,
        currentBook: oldState.books.get((assessment?.grades && assessment?.grades[nextLevel]?.bookId) || ""),
        currentPage: oldState.pages
          .get((assessment?.grades && assessment.grades[nextLevel]?.bookId) || "")
          ?.find(page => page.navigation.pageNum === oldState.currentScreen + 1),
      };
    });
  };

  let canAdvance = true;
  const lastPage = state.currentScreen === assessment.numScreens;

  // We can only advance if we're not waiting on a transcript
  if (state.isOnWaitEntry && state.currentScreen > 1) {
    canAdvance = hasPreviousResults();
  }

  return (
    <div style={{ backgroundColor: "#f5f2ed", height: "100vh" }}>
      <StudentHeader />
      {!state.assessmentCompleted && (
        <div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ maxWidth: 600, paddingTop: 30 }}>
              <Typography className="pageTitle">
                Page {state.currentScreen} of {assessment.numScreens}
              </Typography>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <div style={{ maxWidth: 600, padding: 30 }}>
              <ReaderView
                fontSize={Math.max(props.userSettings?.readerFontSize || 28, 8)}
                viewType="text"
                bookName=""
                pageNumber={1}
                page={state.currentPage}
              />
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            {!state.isRecording && (
              <BookScreenButton text="Start" tooltip="Begin the Assessment" onPressed={startRecording} />
            )}
            {state.isRecording && !lastPage && (
              <BookScreenButton
                disabled={!canAdvance}
                text="Next"
                tooltip="Continue to Next Assessment"
                onPressed={nextRecording}
              />
            )}
            {state.isRecording && lastPage && (
              <BookScreenButton
                text="Finished"
                tooltip="Complete Assessment"
                onPressed={async () => {
                  stopRecording(true);
                  await FirebaseService.manageStudentOnAssignment(
                    props.userId,
                    state.currentGradeLevel?.targetClassId || "",
                    state.currentGradeLevel?.targetAssignmentId || "",
                    true,
                  );
                  await FirebaseService.updateReadleeProfile(props.userId, "needsAssessment", false);
                  await FirebaseService.updateReadleeProfile(
                    props.userId,
                    "assessmentLevel",
                    state.currentAssessmentLevel,
                  );
                  setState(oldState => {
                    return {
                      ...oldState,
                      assessmentCompleted: true,
                    };
                  });
                }}
              />
            )}
          </div>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
        {state.assessmentCompleted && (
          <Paper style={{ padding: 20, textAlign: "center" }}>
            <Typography variant="h5">Completed</Typography>
            <hr />
            Thank you for taking that assessment, there is now a new book in your library, chosen just for you.
          </Paper>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state: RootState) => {
  return {
    profile: state.currentUserState.currentUser,
    userId: state.currentUserState.currentUser?.uid || "",
    isConnectingUser: state.currentUserState.isConnectingUser,
    isLoggedIn: state.currentUserState.currentUser !== undefined,
    isConnectingSettings: state.currentUserState.isConnectingSettings,
    userSettings: state.currentUserState.currentUserSettings,
    informationModalState: state.informationModalState,
  };
};
export default connect(mapStateToProps)(AssessmentScreen);
